/**
 * Client ของ Kanban/Pull (D-102) — replenishment policy + control cycle + card
 * (`/v1/replenishment-policies`, โมดูล `KANBAN_CONFIG`) และ kanban signal
 * (`/v1/kanban-signals`, โมดูล `KANBAN_EXECUTION`) ตาม payload contract ที่ freeze
 * ไว้ใน `apps/api/.../modules/kanban/schemas.py`
 *
 * **ไฟล์เดียวสำหรับสองกลุ่ม** เหมือน `lib/procurement/client.ts` โดยตั้งใจเดียวกัน:
 * ทั้งคู่ตอบ error envelope รูปเดียวกันและใช้ท่อคำขอเดียวกัน (`requestPayload`) —
 * ต่างจากใบขอซื้อ/ใบสั่งซื้อตรงที่ **สองกลุ่มนี้ถือคนละโมดูลสิทธิ์** (D-102 ข้อ 1)
 * จึงมี `KanbanFailure` ชุดเดียวร่วมกันแต่ไม่มีฟังก์ชันใดข้ามกลุ่มไปเรียกอีกกลุ่มเลย —
 * สองหน้าจอที่เรียกไฟล์นี้ (`ReplenishmentPolicyScreen`, `KanbanSignalScreen`) จึง
 * import เฉพาะฟังก์ชันของกลุ่มตัวเอง
 *
 * หลักการเดียวกับ `lib/procurement/client.ts`:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 * - payload ที่ผิดรูปคือ `invalid-response` **ไม่ใช่ "ยังไม่มีข้อมูล"**
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CancelKanbanSignalBody,
  CreateControlCycleBody,
  CreateReplenishmentPolicyBody,
  IssueCardBody,
  KanbanCard,
  KanbanContainerLot,
  KanbanControlCycle,
  KanbanControlCycleListPage,
  KanbanSignal,
  KanbanSignalListPage,
  KanbanSignalTransitionBody,
  PolicyTransitionBody,
  ReplenishKanbanSignalBody,
  ReplenishmentPolicy,
  ReplenishmentPolicyListPage,
  ScanKanbanCardEmptyBody,
  StartKanbanReplenishmentBody,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/** `docs/API_CONTRACT.md` — กลุ่ม "Kanban configuration" (`KANBAN_CONFIG`) */
export const REPLENISHMENT_POLICIES_PATH = '/v1/replenishment-policies';

/** `docs/API_CONTRACT.md` — กลุ่ม "Kanban execution" (`KANBAN_EXECUTION`) */
export const KANBAN_SIGNALS_PATH = '/v1/kanban-signals';

export function replenishmentPolicyPath(policyId: string): string {
  return `${REPLENISHMENT_POLICIES_PATH}/${encodeURIComponent(policyId)}`;
}

export function kanbanControlCyclesPath(policyId: string): string {
  return `${replenishmentPolicyPath(policyId)}/control-cycles`;
}

export function kanbanCardsPath(policyId: string, cycleId: string): string {
  return `${kanbanControlCyclesPath(policyId)}/${encodeURIComponent(cycleId)}/cards`;
}

export function kanbanSignalPath(signalId: string): string {
  return `${KANBAN_SIGNALS_PATH}/${encodeURIComponent(signalId)}`;
}

/** สอง transition ของ policy — ชื่อ segment ตรงตามที่ contract เขียนไว้ */
export type PolicyTransition = 'approve' | 'cancel';

export function policyTransitionPath(policyId: string, transition: PolicyTransition): string {
  return `${replenishmentPolicyPath(policyId)}/${transition}`;
}

/** สี่ transition ของ signal — ชื่อ segment ตรงตามที่ contract เขียนไว้ */
export type KanbanSignalTransition =
  | 'acknowledge'
  | 'start-replenishment'
  | 'replenish'
  | 'cancel';

export function kanbanSignalTransitionPath(
  signalId: string,
  transition: KanbanSignalTransition,
): string {
  return `${kanbanSignalPath(signalId)}/${transition}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /** `details.rule` — วิธีที่ contract แยกกรณีทางธุรกิจออกจากกันโดยไม่เพิ่ม top-level code */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ แยกเป็นสถานะของตัวเอง — ใช้ร่วมกันทั้งสอง
 * กลุ่ม (policy/cycle/card และ signal) เพราะ envelope และรหัส HTTP ชุดเดียวกัน
 *
 * `conflict` (`409`) กับ `rule-violation` (`422`) **ต้องแยกกัน ห้ามยุบรวม** — นี่คือ
 * จุดที่ headline ของ `WO-P2-M7-006` อยู่: บนเส้นทาง signal `409
 * SIGNAL_STATUS_CONFLICT` คือแถวขยับใต้มือ (ลองใหม่ได้) ส่วน `422
 * SIGNAL_IS_NOT_OPEN` คือ signal จบไปแล้ว (ลองใหม่ไม่มีทางสำเร็จ) — การแยก
 * `PurchaseRequisitionFailure['status']` ตาม HTTP code จึงพอสำหรับให้หน้าจอ render
 * สองแบบต่างกันโดยไม่ต้องอ่าน `rule` ก่อน (ดู `KanbanSignalScreen.tsx`)
 */
export type KanbanFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export interface KanbanRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `apiFetch` ตัวเดียวกับ client อื่น */
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

function requiredInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

function nullableInteger(value: unknown): number | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'number' && Number.isInteger(value) ? value : undefined;
}

function parseCursor(raw: unknown): string | null | undefined {
  const cursor = nullableString(raw);
  if (cursor === undefined) {
    return undefined;
  }
  return cursor === null || cursor.trim() === '' ? null : cursor;
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/**
 * หนึ่ง policy — **บังคับครบทุกฟิลด์ที่ contract บังคับ**
 *
 * `status`/`mode` อ่านเป็นสตริงอะไรก็ได้ที่ไม่ว่าง ไม่ใช่ union ที่หน้าจอรู้จักตายตัว
 * — CHECK constraint ฝั่งเซิร์ฟเวอร์เป็นผู้ถือชุดที่รับได้ (เหตุผลเดียวกับ
 * `parsePurchaseRequisition`)
 */
export function parseReplenishmentPolicy(raw: unknown): ReplenishmentPolicy | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const policyId = requiredString(record.policy_id);
  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const plantId = requiredString(record.plant_id);
  const plantCode = requiredString(record.plant_code);
  const sourceLocationId = requiredString(record.source_location_id);
  const sourceLocationCode = requiredString(record.source_location_code);
  const destinationLocationId = requiredString(record.destination_location_id);
  const destinationLocationCode = requiredString(record.destination_location_code);
  const mode = requiredString(record.mode);
  const containerQty = requiredString(record.container_qty);
  const cardCount = requiredInteger(record.card_count);
  const status = requiredString(record.status);
  const effectiveFrom = requiredString(record.effective_from);
  const revisionNo = requiredInteger(record.revision_no);
  const createdBy = requiredString(record.created_by_employee_id);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    policyId === null ||
    productId === null ||
    productCode === null ||
    plantId === null ||
    plantCode === null ||
    sourceLocationId === null ||
    sourceLocationCode === null ||
    destinationLocationId === null ||
    destinationLocationCode === null ||
    mode === null ||
    containerQty === null ||
    cardCount === null ||
    status === null ||
    effectiveFrom === null ||
    revisionNo === null ||
    createdBy === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  const upstreamWorkCenterId = nullableString(record.upstream_work_center_id);
  const upstreamWorkCenterCode = nullableString(record.upstream_work_center_code);
  const downstreamWorkCenterId = nullableString(record.downstream_work_center_id);
  const downstreamWorkCenterCode = nullableString(record.downstream_work_center_code);
  const effectiveTo = nullableString(record.effective_to);
  const supersededByPolicyId = nullableString(record.superseded_by_policy_id);
  const approvedBy = nullableString(record.approved_by_employee_id);
  const approvedAt = nullableString(record.approved_at);
  const note = nullableString(record.note);

  if (
    upstreamWorkCenterId === undefined ||
    upstreamWorkCenterCode === undefined ||
    downstreamWorkCenterId === undefined ||
    downstreamWorkCenterCode === undefined ||
    effectiveTo === undefined ||
    supersededByPolicyId === undefined ||
    approvedBy === undefined ||
    approvedAt === undefined ||
    note === undefined
  ) {
    return null;
  }

  return {
    policy_id: policyId,
    product_id: productId,
    product_code: productCode,
    plant_id: plantId,
    plant_code: plantCode,
    source_location_id: sourceLocationId,
    source_location_code: sourceLocationCode,
    destination_location_id: destinationLocationId,
    destination_location_code: destinationLocationCode,
    upstream_work_center_id: upstreamWorkCenterId,
    upstream_work_center_code: upstreamWorkCenterCode,
    downstream_work_center_id: downstreamWorkCenterId,
    downstream_work_center_code: downstreamWorkCenterCode,
    mode,
    container_qty: containerQty,
    card_count: cardCount,
    status,
    effective_from: effectiveFrom,
    effective_to: effectiveTo,
    revision_no: revisionNo,
    superseded_by_policy_id: supersededByPolicyId,
    created_by_employee_id: createdBy,
    approved_by_employee_id: approvedBy,
    approved_at: approvedAt,
    note,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function parseReplenishmentPolicyListPage(
  raw: unknown,
): ReplenishmentPolicyListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: ReplenishmentPolicy[] = [];
  for (const entry of record.items) {
    const parsed = parseReplenishmentPolicy(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

export function parseKanbanControlCycle(raw: unknown): KanbanControlCycle | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const cycleId = requiredString(record.cycle_id);
  const policyId = requiredString(record.policy_id);
  const cardCount = requiredInteger(record.card_count);
  const status = requiredString(record.status);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    cycleId === null ||
    policyId === null ||
    cardCount === null ||
    status === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  return {
    cycle_id: cycleId,
    policy_id: policyId,
    card_count: cardCount,
    status,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function parseKanbanControlCycleListPage(
  raw: unknown,
): KanbanControlCycleListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: KanbanControlCycle[] = [];
  for (const entry of record.items) {
    const parsed = parseKanbanControlCycle(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }
  return { items };
}

export function parseKanbanCard(raw: unknown): KanbanCard | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const cardId = requiredString(record.card_id);
  const cycleId = requiredString(record.cycle_id);
  const cardCode = requiredString(record.card_code);
  const tagSchemaVersion = requiredInteger(record.tag_schema_version);
  const status = requiredString(record.status);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    cardId === null ||
    cycleId === null ||
    cardCode === null ||
    tagSchemaVersion === null ||
    status === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  return {
    card_id: cardId,
    cycle_id: cycleId,
    card_code: cardCode,
    tag_schema_version: tagSchemaVersion,
    status,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

function parseKanbanContainerLot(raw: unknown): KanbanContainerLot | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const lotId = requiredString(record.lot_id);
  const qty = requiredString(record.qty);
  const createdAt = requiredString(record.created_at);

  if (lotId === null || qty === null || createdAt === null) {
    return null;
  }
  return { lot_id: lotId, qty, created_at: createdAt };
}

/**
 * หนึ่ง signal — **บังคับครบทุกฟิลด์ที่ contract บังคับ** รวม `container_lots` ซึ่งเป็น
 * array ว่างได้แต่ต้อง**อ่านออกทุกรายการ** ไม่งั้นทั้ง signal ผิดรูป (เหตุผลเดียวกับ
 * `parsePurchaseOrder` ที่บรรทัดขาดหนึ่งบรรทัดทำให้ทั้งใบผิดรูป)
 */
export function parseKanbanSignal(raw: unknown): KanbanSignal | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const signalId = requiredString(record.signal_id);
  const cardId = requiredString(record.card_id);
  const cardCode = requiredString(record.card_code);
  const cardStatus = requiredString(record.card_status);
  const cycleId = requiredString(record.cycle_id);
  const policyId = requiredString(record.policy_id);
  const plantId = requiredString(record.plant_id);
  const plantCode = requiredString(record.plant_code);
  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const status = requiredString(record.status);
  const raisedBy = requiredString(record.raised_by_employee_id);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    signalId === null ||
    cardId === null ||
    cardCode === null ||
    cardStatus === null ||
    cycleId === null ||
    policyId === null ||
    plantId === null ||
    plantCode === null ||
    productId === null ||
    productCode === null ||
    status === null ||
    raisedBy === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  const clientEventId = nullableString(record.client_event_id);
  const deviceId = nullableString(record.device_id);
  const deviceSequenceNo = nullableInteger(record.device_sequence_no);
  const supplyDocumentType = nullableString(record.supply_document_type);
  const supplyDocumentId = nullableString(record.supply_document_id);
  const cancelledBy = nullableString(record.cancelled_by_employee_id);
  const cancellationReason = nullableString(record.cancellation_reason);
  const cancelledAt = nullableString(record.cancelled_at);

  if (
    clientEventId === undefined ||
    deviceId === undefined ||
    deviceSequenceNo === undefined ||
    supplyDocumentType === undefined ||
    supplyDocumentId === undefined ||
    cancelledBy === undefined ||
    cancellationReason === undefined ||
    cancelledAt === undefined
  ) {
    return null;
  }

  if (!Array.isArray(record.container_lots)) {
    return null;
  }
  const containerLots: KanbanContainerLot[] = [];
  for (const entry of record.container_lots) {
    const lot = parseKanbanContainerLot(entry);
    if (lot === null) {
      return null;
    }
    containerLots.push(lot);
  }

  return {
    signal_id: signalId,
    card_id: cardId,
    card_code: cardCode,
    card_status: cardStatus,
    cycle_id: cycleId,
    policy_id: policyId,
    plant_id: plantId,
    plant_code: plantCode,
    product_id: productId,
    product_code: productCode,
    status,
    raised_by_employee_id: raisedBy,
    client_event_id: clientEventId,
    device_id: deviceId,
    device_sequence_no: deviceSequenceNo,
    supply_document_type: supplyDocumentType,
    supply_document_id: supplyDocumentId,
    cancelled_by_employee_id: cancelledBy,
    cancellation_reason: cancellationReason,
    cancelled_at: cancelledAt,
    container_lots: containerLots,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function parseKanbanSignalListPage(raw: unknown): KanbanSignalListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: KanbanSignal[] = [];
  for (const entry of record.items) {
    const parsed = parseKanbanSignal(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

/* ---------------------------------------------------------- serializing -- */

export function serializeCreateReplenishmentPolicyBody(
  body: CreateReplenishmentPolicyBody,
): Record<string, unknown> {
  return {
    plant_code: body.plant_code,
    product_code: body.product_code,
    source_location_code: body.source_location_code,
    destination_location_code: body.destination_location_code,
    ...(body.upstream_work_center_code === undefined
      ? {}
      : { upstream_work_center_code: body.upstream_work_center_code }),
    ...(body.downstream_work_center_code === undefined
      ? {}
      : { downstream_work_center_code: body.downstream_work_center_code }),
    mode: body.mode,
    container_qty: body.container_qty,
    card_count: body.card_count,
    effective_from: body.effective_from,
    ...(body.effective_to === undefined ? {} : { effective_to: body.effective_to }),
    ...(body.note === undefined ? {} : { note: body.note }),
  };
}

export function serializePolicyTransitionBody(
  body: PolicyTransitionBody = {},
): Record<string, unknown> {
  return body.expected_status === undefined ? {} : { expected_status: body.expected_status };
}

export function serializeCreateControlCycleBody(
  body: CreateControlCycleBody,
): Record<string, unknown> {
  return { card_count: body.card_count };
}

export function serializeIssueCardBody(body: IssueCardBody): Record<string, unknown> {
  return { card_code: body.card_code };
}

export function serializeScanKanbanCardEmptyBody(
  body: ScanKanbanCardEmptyBody,
): Record<string, unknown> {
  return {
    card_code: body.card_code,
    ...(body.client_event_id === undefined ? {} : { client_event_id: body.client_event_id }),
  };
}

export function serializeKanbanSignalTransitionBody(
  body: KanbanSignalTransitionBody = {},
): Record<string, unknown> {
  return body.expected_status === undefined ? {} : { expected_status: body.expected_status };
}

export function serializeStartKanbanReplenishmentBody(
  body: StartKanbanReplenishmentBody,
): Record<string, unknown> {
  return {
    supply_document_type: body.supply_document_type,
    supply_document_id: body.supply_document_id,
    ...(body.expected_status === undefined ? {} : { expected_status: body.expected_status }),
  };
}

export function serializeCancelKanbanSignalBody(
  body: CancelKanbanSignalBody,
): Record<string, unknown> {
  return {
    cancellation_reason: body.cancellation_reason,
    ...(body.expected_status === undefined ? {} : { expected_status: body.expected_status }),
  };
}

export function serializeReplenishKanbanSignalBody(
  body: ReplenishKanbanSignalBody,
): Record<string, unknown> {
  return {
    lots: body.lots.map((lot) => ({ lot_id: lot.lot_id, qty: lot.qty })),
    ...(body.expected_status === undefined ? {} : { expected_status: body.expected_status }),
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult = { readonly status: 'payload'; readonly payload: unknown } | KanbanFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: KanbanRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/* ===================================== Kanban configuration (KANBAN_CONFIG) === */

export interface ReplenishmentPolicyListQuery {
  readonly plant_code?: string;
  readonly status?: string;
  readonly mode?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

function buildReplenishmentPolicyListQuery(query: ReplenishmentPolicyListQuery = {}): string {
  const params = new URLSearchParams();
  const plantCode = query.plant_code?.trim() ?? '';
  if (plantCode !== '') {
    params.append('plant_code', plantCode);
  }
  const status = query.status?.trim() ?? '';
  if (status !== '') {
    params.append('status', status);
  }
  const mode = query.mode?.trim() ?? '';
  if (mode !== '') {
    params.append('mode', mode);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

export type ReplenishmentPolicyListResult =
  | { readonly status: 'ready'; readonly page: ReplenishmentPolicyListPage }
  | KanbanFailure;

export type ReplenishmentPolicyWriteResult =
  | { readonly status: 'ready'; readonly policy: ReplenishmentPolicy }
  | KanbanFailure;

function toPolicyWriteResult(result: PayloadResult): ReplenishmentPolicyWriteResult {
  if (result.status !== 'payload') {
    return result;
  }
  const policy = parseReplenishmentPolicy(result.payload);
  return policy === null ? { status: 'invalid-response' } : { status: 'ready', policy };
}

/** `GET /v1/replenishment-policies` — กรองตามขอบเขตโรงงานของผู้เรียกเสมอ */
export async function fetchReplenishmentPolicies(
  query: ReplenishmentPolicyListQuery = {},
  options: KanbanRequestOptions = {},
): Promise<ReplenishmentPolicyListResult> {
  const result = await requestPayload(
    `${REPLENISHMENT_POLICIES_PATH}${buildReplenishmentPolicyListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const page = parseReplenishmentPolicyListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** `POST /v1/replenishment-policies` — สร้าง revision ใหม่ที่ `DRAFT` เสมอ ตอบ `201` */
export async function createReplenishmentPolicy(
  body: CreateReplenishmentPolicyBody,
  options: KanbanRequestOptions = {},
): Promise<ReplenishmentPolicyWriteResult> {
  return toPolicyWriteResult(
    await requestPayload(
      REPLENISHMENT_POLICIES_PATH,
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeCreateReplenishmentPolicyBody(body)),
      },
      options,
    ),
  );
}

async function requestPolicyTransition(
  policyId: string,
  transition: PolicyTransition,
  body: PolicyTransitionBody,
  options: KanbanRequestOptions,
): Promise<ReplenishmentPolicyWriteResult> {
  return toPolicyWriteResult(
    await requestPayload(
      policyTransitionPath(policyId, transition),
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializePolicyTransitionBody(body)),
      },
      options,
    ),
  );
}

/**
 * `POST /v1/replenishment-policies/{policy_id}/approve` — `DRAFT → ACTIVE` (APPROVE)
 *
 * **ผู้อนุมัติต้องไม่ใช่ผู้สร้าง** (D-010) — `422 APPROVER_MUST_DIFFER_FROM_CREATOR`
 * และ **ไม่มีอะไรถูกเขียนเลย**
 */
export async function approveReplenishmentPolicy(
  policyId: string,
  body: PolicyTransitionBody = {},
  options: KanbanRequestOptions = {},
): Promise<ReplenishmentPolicyWriteResult> {
  return requestPolicyTransition(policyId, 'approve', body, options);
}

/** `POST /v1/replenishment-policies/{policy_id}/cancel` — `DRAFT → CANCELLED` (EDIT) */
export async function cancelReplenishmentPolicy(
  policyId: string,
  body: PolicyTransitionBody = {},
  options: KanbanRequestOptions = {},
): Promise<ReplenishmentPolicyWriteResult> {
  return requestPolicyTransition(policyId, 'cancel', body, options);
}

export type KanbanControlCycleListResult =
  | { readonly status: 'ready'; readonly page: KanbanControlCycleListPage }
  | KanbanFailure;

export type KanbanControlCycleWriteResult =
  | { readonly status: 'ready'; readonly cycle: KanbanControlCycle }
  | KanbanFailure;

/** `GET /v1/replenishment-policies/{policy_id}/control-cycles` — ใหม่สุดก่อน ไม่แบ่งหน้า */
export async function fetchKanbanControlCycles(
  policyId: string,
  options: KanbanRequestOptions = {},
): Promise<KanbanControlCycleListResult> {
  const result = await requestPayload(
    kanbanControlCyclesPath(policyId),
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const page = parseKanbanControlCycleListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `POST /v1/replenishment-policies/{policy_id}/control-cycles` — ตัด cycle บน policy
 * ที่ `ACTIVE` (`409 POLICY_IS_NOT_ACTIVE` ถ้ายังไม่ใช่) ตอบ `201`
 */
export async function createKanbanControlCycle(
  policyId: string,
  body: CreateControlCycleBody,
  options: KanbanRequestOptions = {},
): Promise<KanbanControlCycleWriteResult> {
  const result = await requestPayload(
    kanbanControlCyclesPath(policyId),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeCreateControlCycleBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const cycle = parseKanbanControlCycle(result.payload);
  return cycle === null ? { status: 'invalid-response' } : { status: 'ready', cycle };
}

export type KanbanCardWriteResult =
  | { readonly status: 'ready'; readonly card: KanbanCard }
  | KanbanFailure;

/**
 * `POST .../control-cycles/{cycle_id}/cards` — ออกการ์ดใหม่บน cycle ที่ `ACTIVE`
 * (`409 CYCLE_IS_NOT_ACTIVE`), เกินจำนวนที่ cycle ประกาศไว้ตอบ
 * `422 CYCLE_CARD_COUNT_EXCEEDED`, รหัสซ้ำตอบ `409 CARD_CODE_ALREADY_ISSUED` — ตอบ `201`
 */
export async function issueKanbanCard(
  policyId: string,
  cycleId: string,
  body: IssueCardBody,
  options: KanbanRequestOptions = {},
): Promise<KanbanCardWriteResult> {
  const result = await requestPayload(
    kanbanCardsPath(policyId, cycleId),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeIssueCardBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const card = parseKanbanCard(result.payload);
  return card === null ? { status: 'invalid-response' } : { status: 'ready', card };
}

/* ========================================== Kanban execution (KANBAN_EXECUTION) === */

export interface KanbanSignalListQuery {
  readonly plant_code?: string;
  readonly card_code?: string;
  readonly status?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

function buildKanbanSignalListQuery(query: KanbanSignalListQuery = {}): string {
  const params = new URLSearchParams();
  const plantCode = query.plant_code?.trim() ?? '';
  if (plantCode !== '') {
    params.append('plant_code', plantCode);
  }
  const cardCode = query.card_code?.trim() ?? '';
  if (cardCode !== '') {
    params.append('card_code', cardCode);
  }
  const status = query.status?.trim() ?? '';
  if (status !== '') {
    params.append('status', status);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

export type KanbanSignalListResult =
  | { readonly status: 'ready'; readonly page: KanbanSignalListPage }
  | KanbanFailure;

export type KanbanSignalWriteResult =
  | { readonly status: 'ready'; readonly signal: KanbanSignal }
  | KanbanFailure;

function toSignalWriteResult(result: PayloadResult): KanbanSignalWriteResult {
  if (result.status !== 'payload') {
    return result;
  }
  const signal = parseKanbanSignal(result.payload);
  return signal === null ? { status: 'invalid-response' } : { status: 'ready', signal };
}

/** `GET /v1/kanban-signals` — บอร์ด Kanban ทั้งหมดที่ผู้เรียกมีขอบเขตโรงงานถึง */
export async function fetchKanbanSignals(
  query: KanbanSignalListQuery = {},
  options: KanbanRequestOptions = {},
): Promise<KanbanSignalListResult> {
  const result = await requestPayload(
    `${KANBAN_SIGNALS_PATH}${buildKanbanSignalListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const page = parseKanbanSignalListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `POST /v1/kanban-signals/empty` — สแกนการ์ดเบิก ยก signal ใหม่ที่ `NEW` ตอบ `201`
 *
 * `409 CARD_ALREADY_HAS_AN_OPEN_SIGNAL` เมื่อการ์ดนี้มี signal เปิดอยู่แล้ว,
 * `422 PART_ALREADY_COVERED_BY_PUSH_SUPPLY` เมื่อชิ้นนี้ยังมี Push supply เปิดอยู่
 * (D-102 ข้อ 8b)
 */
export async function scanKanbanCardEmpty(
  body: ScanKanbanCardEmptyBody,
  options: KanbanRequestOptions = {},
): Promise<KanbanSignalWriteResult> {
  return toSignalWriteResult(
    await requestPayload(
      `${KANBAN_SIGNALS_PATH}/empty`,
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeScanKanbanCardEmptyBody(body)),
      },
      options,
    ),
  );
}

async function requestSignalTransition(
  signalId: string,
  transition: KanbanSignalTransition,
  body: Record<string, unknown>,
  options: KanbanRequestOptions,
): Promise<KanbanSignalWriteResult> {
  return toSignalWriteResult(
    await requestPayload(
      kanbanSignalTransitionPath(signalId, transition),
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      options,
    ),
  );
}

/**
 * `POST /v1/kanban-signals/{signal_id}/acknowledge` — `NEW → ACKNOWLEDGED`
 *
 * การ์ดยังอยู่ที่ `EMPTY` — รับทราบไม่ได้แปลว่ากำลังผลิตอะไร (ยังไม่แตะ ledger)
 */
export async function acknowledgeKanbanSignal(
  signalId: string,
  body: KanbanSignalTransitionBody = {},
  options: KanbanRequestOptions = {},
): Promise<KanbanSignalWriteResult> {
  return requestSignalTransition(
    signalId,
    'acknowledge',
    serializeKanbanSignalTransitionBody(body),
    options,
  );
}

/**
 * `POST /v1/kanban-signals/{signal_id}/start-replenishment` — `ACKNOWLEDGED →
 * IN_REPLENISHMENT` การ์ดขยับไป `REPLENISHING` — บันทึกว่าเอกสารใดกำลังเติมของ
 * (ไม่แตะ ledger — เอกสารที่อ้างเป็นผู้เคลื่อนสต๊อกจริง)
 */
export async function startKanbanReplenishment(
  signalId: string,
  body: StartKanbanReplenishmentBody,
  options: KanbanRequestOptions = {},
): Promise<KanbanSignalWriteResult> {
  return requestSignalTransition(
    signalId,
    'start-replenishment',
    serializeStartKanbanReplenishmentBody(body),
    options,
  );
}

/**
 * `POST /v1/kanban-signals/{signal_id}/replenish` — `IN_REPLENISHMENT →
 * REPLENISHED` การ์ดขยับไป `FULL` — **เส้นทางเดียวที่แตะ ledger จริง** ไม่ใช่ที่นี่
 * โดยตรง (การโพสต์อยู่ที่เอกสารที่ `start-replenishment` อ้างถึง) แต่เป็นเส้นทางเดียว
 * ที่ยืนยันว่ามีของเข้าภาชนะจริงพร้อม lot
 */
export async function replenishKanbanSignal(
  signalId: string,
  body: ReplenishKanbanSignalBody,
  options: KanbanRequestOptions = {},
): Promise<KanbanSignalWriteResult> {
  return requestSignalTransition(
    signalId,
    'replenish',
    serializeReplenishKanbanSignalBody(body),
    options,
  );
}

/**
 * `POST /v1/kanban-signals/{signal_id}/cancel` — เฉพาะ signal ที่ยังเปิดอยู่
 * (`NEW`/`ACKNOWLEDGED`/`IN_REPLENISHMENT`) `422 SIGNAL_IS_NOT_OPEN` เมื่อจบไปแล้ว —
 * **ต้องมีเหตุผลเสมอ** (`WO-P2-M7-005`) การ์ดกลับ `AVAILABLE` เว้นแต่ถูก `BLOCKED` ไว้
 */
export async function cancelKanbanSignal(
  signalId: string,
  body: CancelKanbanSignalBody,
  options: KanbanRequestOptions = {},
): Promise<KanbanSignalWriteResult> {
  return requestSignalTransition(
    signalId,
    'cancel',
    serializeCancelKanbanSignalBody(body),
    options,
  );
}

/**
 * อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี
 *
 * ไม่เดาค่าเมื่ออ่านไม่ได้ เพราะกฎที่เดาผิดจะส่งผู้ใช้ไปแก้เรื่องที่ไม่ได้เสีย
 */
export function ruleFromFailure(failure: KanbanFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}

/** อ่าน `details.current_status` จากความล้มเหลว — สถานะที่แถวนั้น **ถืออยู่จริง** */
export function currentStatusFromFailure(failure: KanbanFailure): string | null {
  if (!('error' in failure)) {
    return null;
  }
  const value = failure.error.details.current_status;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}
